import { useEffect, useState } from "react";
import {
  Plus,
  PencilSimple,
  Trash,
  CircleNotch,
  X,
  Eye,
  EyeSlash,
  FilePdf,
} from "@phosphor-icons/react";
import {
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
} from "@/lib/api";
import FileUploadField from "./FileUploadField";

const EMPTY = {
  title: "",
  subtitle: "",
  description: "",
  image_url: null,
  badge: "",
  brochure_url: null,
  brochure_title: "",
  sort_order: 0,
  is_published: true,
};

export default function ProductsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | 'new' | product object
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    listProducts({ all: true })
      .then((r) => setItems(r.items || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (p) => setEditing({ ...p });
  const cancelEdit = () => setEditing(null);

  const save = async (payload) => {
    setBusy(true);
    setError("");
    try {
      if (payload.id) {
        await updateProduct(payload.id, payload);
      } else {
        await createProduct(payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteProduct(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Catalogo prodotti</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-medium">
            {items.length}{" "}
            <span className="text-[#5C5A55] font-light">
              {items.length === 1 ? "prodotto" : "prodotti"}
            </span>
          </h2>
        </div>
        <button
          onClick={startNew}
          data-testid="products-add-btn"
          className="btn-primary"
        >
          <Plus size={16} weight="bold" />
          Nuovo prodotto
        </button>
      </div>

      {error && (
        <div className="p-3 bg-[#EB0D0D]/8 text-[#C00A0A] text-sm border border-[#EB0D0D]/30">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <CircleNotch
            size={28}
            weight="bold"
            className="animate-spin text-[#EB0D0D]"
          />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#F0EFEB] border border-[#D1CFC7]/40 py-16 text-center text-[#5C5A55]">
          Nessun prodotto ancora. Clicca "Nuovo prodotto" per iniziare.
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-products-list">
          {items.map((p) => (
            <div
              key={p.id}
              className={`bg-[#F0EFEB] p-5 border border-[#D1CFC7]/40 grid grid-cols-1 md:grid-cols-12 gap-4 items-center ${
                !p.is_published ? "opacity-60" : ""
              }`}
              data-testid={`product-row-${p.id}`}
            >
              <div className="md:col-span-1">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt=""
                    className="w-16 h-16 object-cover border border-[#D1CFC7]"
                  />
                ) : (
                  <div className="w-16 h-16 bg-[#E2E0D8] border border-[#D1CFC7]" />
                )}
              </div>
              <div className="md:col-span-6 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {p.badge && (
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold bg-[#1A1918] text-[#FAF9F6] px-2 py-0.5">
                      {p.badge}
                    </span>
                  )}
                  {!p.is_published && (
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold bg-[#5C5A55] text-[#FAF9F6] px-2 py-0.5">
                      Nascosto
                    </span>
                  )}
                  {p.brochure_url && (
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#EB0D0D] inline-flex items-center gap-1">
                      <FilePdf size={12} weight="bold" />
                      Brochure
                    </span>
                  )}
                </div>
                <div className="font-display text-lg truncate">{p.title}</div>
                {p.subtitle && (
                  <div className="text-xs text-[#5C5A55] truncate">
                    {p.subtitle}
                  </div>
                )}
              </div>
              <div className="md:col-span-3 text-sm text-[#5C5A55] line-clamp-2">
                {p.description || <span className="italic">Nessuna descrizione</span>}
              </div>
              <div className="md:col-span-2 flex md:justify-end gap-1">
                <button
                  onClick={() => startEdit(p)}
                  className="p-2 hover:bg-[#FAF9F6] text-[#1A1918] transition-colors"
                  title="Modifica"
                  data-testid={`edit-product-${p.id}`}
                >
                  <PencilSimple size={16} weight="regular" />
                </button>
                <button
                  onClick={() => setDeleteTarget(p)}
                  className="p-2 hover:bg-[#EB0D0D]/10 text-[#EB0D0D] transition-colors"
                  title="Elimina"
                  data-testid={`delete-product-${p.id}`}
                >
                  <Trash size={16} weight="regular" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ProductEditModal
          initial={editing}
          onSave={save}
          onCancel={cancelEdit}
          busy={busy}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title={deleteTarget.title}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

function ProductEditModal({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial);
  const isNew = !initial.id;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave(form);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
      onClick={busy ? null : onCancel}
    >
      <form
        onSubmit={onSubmit}
        className="bg-[#FAF9F6] w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#D1CFC7] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="product-edit-modal"
      >
        <div className="flex items-start justify-between p-6 md:p-8 border-b border-[#D1CFC7]/60 sticky top-0 bg-[#FAF9F6] z-10">
          <div>
            <div className="overline mb-1">
              {isNew ? "Nuovo prodotto" : "Modifica prodotto"}
            </div>
            <h3 className="font-display text-2xl tracking-tighter font-medium">
              {form.title || "Senza titolo"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="p-2 hover:bg-[#F0EFEB] transition-colors disabled:opacity-50"
            aria-label="Chiudi"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-5">
          <Field label="Titolo *">
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
              data-testid="product-title-input"
            />
          </Field>

          <Field label="Sottotitolo">
            <input
              type="text"
              value={form.subtitle || ""}
              onChange={(e) => update({ subtitle: e.target.value })}
              placeholder="Es. Win · Win Slide · Lumen · Fiber Slim"
              className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
            />
          </Field>

          <Field label="Descrizione">
            <textarea
              rows={4}
              value={form.description || ""}
              onChange={(e) => update({ description: e.target.value })}
              className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D] resize-none"
            />
          </Field>

          <FileUploadField
            kind="image"
            label="Immagine principale"
            value={form.image_url}
            onChange={(url) => update({ image_url: url })}
            hint="JPG / PNG / WebP · max 5MB"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Badge (opzionale)">
              <input
                type="text"
                value={form.badge || ""}
                onChange={(e) => update({ badge: e.target.value })}
                placeholder="Es. Bestseller, Premium"
                className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
              />
            </Field>
            <Field label="Ordine di visualizzazione">
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  update({ sort_order: parseInt(e.target.value, 10) || 0 })
                }
                className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
              />
            </Field>
          </div>

          <div className="border-t border-[#D1CFC7]/60 pt-5 space-y-4">
            <div className="overline text-[#1A1918]" style={{ color: "#1A1918" }}>
              Brochure PDF (opzionale)
            </div>
            <FileUploadField
              kind="pdf"
              label="File PDF"
              value={form.brochure_url}
              onChange={(url) => update({ brochure_url: url })}
              hint="max 20MB"
            />
            {form.brochure_url && (
              <Field label="Titolo brochure (mostrato al cliente)">
                <input
                  type="text"
                  value={form.brochure_title || ""}
                  onChange={(e) => update({ brochure_title: e.target.value })}
                  placeholder="Es. Catalogo Win Box"
                  className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3 focus:outline-none focus:border-[#EB0D0D]"
                />
              </Field>
            )}
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.is_published}
              onChange={(e) => update({ is_published: e.target.checked })}
              className="w-4 h-4 accent-[#EB0D0D]"
            />
            <span className="inline-flex items-center gap-2 text-sm text-[#1A1918]">
              {form.is_published ? (
                <Eye size={16} weight="bold" className="text-[#EB0D0D]" />
              ) : (
                <EyeSlash size={16} weight="bold" className="text-[#5C5A55]" />
              )}
              Pubblicato sul sito
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 p-6 md:p-8 border-t border-[#D1CFC7]/60 bg-[#F0EFEB] sticky bottom-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={busy || !form.title.trim()}
            data-testid="product-save-btn"
            className="btn-primary disabled:opacity-50"
          >
            {busy ? (
              <>
                <CircleNotch size={14} weight="bold" className="animate-spin" />
                Salvataggio…
              </>
            ) : (
              <>{isNew ? "Crea prodotto" : "Salva modifiche"}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteConfirmModal({ title, onConfirm, onCancel, busy }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
      onClick={busy ? null : onCancel}
    >
      <div
        className="bg-[#FAF9F6] w-full max-w-md border border-[#D1CFC7] shadow-2xl p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overline mb-3">Conferma eliminazione</div>
        <h3 className="font-display text-2xl tracking-tighter font-medium mb-3">
          Eliminare{" "}
          <em className="italic font-light text-[#EB0D0D]">{title}</em>?
        </h3>
        <p className="text-sm text-[#5C5A55] mb-6">
          Il prodotto verrà rimosso dal sito. Le immagini e i PDF caricati
          restano nel server (non eliminati automaticamente).
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="product-delete-confirm"
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-[#EB0D0D] text-[#FAF9F6] hover:bg-[#C00A0A] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? (
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            ) : (
              <Trash size={14} weight="bold" />
            )}
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] font-bold mb-2 text-[#1A1918]">
        {label}
      </label>
      {children}
    </div>
  );
}
